import { useState } from "react";
import { CaretDown, FileText, Receipt } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { ReportShell, Stat, money } from "./ReportShell";

const ymd = (d) => d.toISOString().slice(0, 10);
const plusDays = (n) => ymd(new Date(Date.now() + n * 864e5));
const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const periodLabel = (p, group) => {
  if (group === "day") return fmt(p);
  const end = new Date(`${p}T12:00:00`);
  end.setDate(end.getDate() + 6);
  return `${fmt(p)} – ${fmt(ymd(end))}`;
};

const Toggle = ({ on, onChange, label, tone, testId }) => (
  <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-zinc-300">
    <Switch
      checked={on}
      onCheckedChange={onChange}
      data-testid={testId}
      className={`${tone} data-[state=unchecked]:bg-zinc-700`}
    />
    {label}
  </label>
);

const Chart = ({ periods, group, metric, showQ, showI }) => {
  const val = (b, k) => (metric === "count" ? b[`${k}_count`] : b[k]);
  const max = Math.max(1, ...periods.map((b) => (showQ ? val(b, "uninvoiced") : 0) + (showI ? val(b, "invoiced") : 0)));
  return (
    <div data-testid="forecast-chart" className="flex h-44 items-end gap-1 rounded-lg border border-zinc-800 bg-zinc-900 p-3">
      {periods.map((b) => {
        const q = showQ ? val(b, "uninvoiced") : 0;
        const i = showI ? val(b, "invoiced") : 0;
        return (
          <div key={b.period} className="group relative flex h-full flex-1 flex-col justify-end" title={`${periodLabel(b.period, group)} · ${metric === "count" ? `${q + i}` : money(q + i)}`}>
            <div className="w-full rounded-t bg-teal-400/80" style={{ height: `${(q / max) * 100}%` }} />
            <div className="w-full bg-sky-500/80" style={{ height: `${(i / max) * 100}%` }} />
            <div className="mt-1 truncate text-center text-[9px] text-zinc-500">{fmt(b.period).replace(/, \d{4}$/, "")}</div>
          </div>
        );
      })}
    </div>
  );
};

export const SalesForecastReport = () => {
  const [from, setFrom] = useState(ymd(new Date()));
  const [to, setTo] = useState(plusDays(90));
  const [group, setGroup] = useState("week");
  const [metric, setMetric] = useState("amount");
  const [showQ, setShowQ] = useState(true);
  const [showI, setShowI] = useState(true);
  const [open, setOpen] = useState({});
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async (g = group) => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/sales-forecast", { params: { from, to, group: g } });
      setData(data);
      setOpen({});
    } finally {
      setBusy(false);
    }
  };
  const t = data?.totals;
  const periods = (data?.periods || []).filter((b) => (showQ && b.uninvoiced_count) || (showI && b.invoiced_count));
  const expected = (showQ ? t?.uninvoiced || 0 : 0) + (showI ? t?.invoiced || 0 : 0);
  const csvRows = periods.flatMap((b) => b.rows.filter((r) => (r.kind === "quote" ? showQ : showI)).map((r) => ({ period: b.period, ...r })));

  return (
    <ReportShell
      id="sales-forecast"
      title="Sales Forecast"
      blurb="Expected sales by week or day: invoices already issued plus Sent/Accepted quotes not yet invoiced, dated by their install date."
      range
      from={from}
      to={to}
      onFrom={setFrom}
      onTo={setTo}
      onGenerate={() => generate()}
      busy={busy}
      generated={!!data}
      csvRows={csvRows}
      csvName={`sales-forecast-${from}-${to}.csv`}
    >
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-zinc-800 bg-zinc-900/60 px-4 py-3">
        <div className="flex overflow-hidden rounded-md border border-zinc-700 text-xs font-semibold">
          {["week", "day"].map((g) => (
            <button key={g} type="button" data-testid={`forecast-group-${g}`} onClick={() => { setGroup(g); generate(g); }}
              className={`px-3 py-1.5 capitalize ${group === g ? "bg-indigo-500/30 text-white" : "text-zinc-400 hover:text-white"}`}>{g}</button>
          ))}
        </div>
        <div className="flex overflow-hidden rounded-md border border-zinc-700 text-xs font-semibold">
          {["amount", "count"].map((m) => (
            <button key={m} type="button" data-testid={`forecast-metric-${m}`} onClick={() => setMetric(m)}
              className={`px-3 py-1.5 capitalize ${metric === m ? "bg-indigo-500/30 text-white" : "text-zinc-400 hover:text-white"}`}>{m}</button>
          ))}
        </div>
        <Toggle on={showQ} onChange={setShowQ} label="Show Quotes (uninvoiced)" tone="data-[state=checked]:bg-teal-500" testId="forecast-toggle-quotes" />
        <Toggle on={showI} onChange={setShowI} label="Show Invoices" tone="data-[state=checked]:bg-sky-500" testId="forecast-toggle-invoices" />
      </div>
      {t && (
        <div className="mt-4 grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
          <div className="space-y-3">
            <Stat label="Expected sales total" value={metric === "count" ? (showQ ? t.uninvoiced_count : 0) + (showI ? t.invoiced_count : 0) : money(expected)} tone="text-white" testId="forecast-stat-expected" />
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 text-sm">
              <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-zinc-300"><span className="h-2.5 w-2.5 rounded-full bg-teal-400" />Uninvoiced</span><span data-testid="forecast-stat-uninvoiced" className="font-mono-data font-bold">{metric === "count" ? t.uninvoiced_count : money(t.uninvoiced)}</span></div>
              <div className="mt-2 flex items-center justify-between"><span className="flex items-center gap-2 text-zinc-300"><span className="h-2.5 w-2.5 rounded-full bg-sky-500" />Invoiced</span><span data-testid="forecast-stat-invoiced" className="font-mono-data font-bold">{metric === "count" ? t.invoiced_count : money(t.invoiced)}</span></div>
            </div>
          </div>
          <Chart periods={periods} group={group} metric={metric} showQ={showQ} showI={showI} />
        </div>
      )}
      {t && (
        <div data-testid="forecast-table" className="mt-4 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900">
          <div className="grid grid-cols-[1fr_1fr_1.4fr_120px] border-b border-zinc-800 px-4 py-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <span>Status &amp; Date</span><span>Billing Contact</span><span>Project</span><span className="text-right">Amount</span>
          </div>
          {periods.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">Nothing expected in this range.</div>}
          {periods.map((b) => {
            const rows = b.rows.filter((r) => (r.kind === "quote" ? showQ : showI));
            const collapsed = open[b.period] === false;
            return (
              <div key={b.period} data-testid={`forecast-period-${b.period}`}>
                <button type="button" onClick={() => setOpen({ ...open, [b.period]: collapsed })}
                  className="flex w-full items-center justify-between bg-zinc-950/60 px-4 py-2 text-left text-sm hover:bg-zinc-800/40">
                  <span className="flex items-center gap-2 font-semibold"><CaretDown size={12} className={collapsed ? "-rotate-90" : ""} />{periodLabel(b.period, group)}<span className="text-xs font-normal text-zinc-500">{rows.length} records</span></span>
                  <span className="font-mono-data text-xs">
                    {showQ && <span className="text-teal-300">{money(b.uninvoiced)}</span>}
                    {showQ && showI && <span className="text-zinc-600"> + </span>}
                    {showI && <span className="text-sky-300">{money(b.invoiced)}</span>}
                    <span className="ml-2 font-bold text-white">{money((showQ ? b.uninvoiced : 0) + (showI ? b.invoiced : 0))}</span>
                  </span>
                </button>
                {!collapsed && rows.map((r) => (
                  <div key={r.id} className={`grid grid-cols-[1fr_1fr_1.4fr_120px] items-center border-t border-zinc-800/60 px-4 py-2 text-sm ${r.kind === "quote" ? "bg-indigo-500/5" : ""}`}>
                    <span className="flex items-center gap-2 text-zinc-300">{r.kind === "quote" ? <FileText size={14} className="text-teal-300" /> : <Receipt size={14} className="text-sky-300" />}{fmt(r.date)}</span>
                    <span className="truncate">{r.contact || "—"}</span>
                    <span className="truncate text-zinc-300">{r.code ? `${r.code} · ` : ""}{r.project || "—"}</span>
                    <span className="font-mono-data text-right">{money(r.amount)}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </ReportShell>
  );
};
